/* @flow */
/* eslint no-alert: off */

import React, { Fragment } from 'react'
import { Map, List } from 'immutable'
import { parseISO } from 'date-fns'
import { nonEmptyString } from 'atpjs/validation/validators'
import i18n from 'atpjs/helpers/i18n'
import Textarea from 'atpjs/components/form/textarea'
import { createOptions } from 'atpjs/components/form/values'
import Step from 'tve/views/step'
import Application from 'tve/components/application'
import DocumentList from 'tve/components/document_list'
import { cancelLead } from 'tve/api/atp'
import { formatTime, dayWithDateTime } from 'tve/helpers/date'

import type { PrefixType } from 'atpjs/helpers/i18n'

const t = i18n.prefix(['confirmation'])

const branchAddress = (branch) => {
  return encodeURIComponent(`${branch.get('street')} ${branch.get('zip')} ${branch.get('city')}, Switzerland`)
}

const replaceLinks = (text) => {
  const urlRegex = /(\b(https?):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi
  return text.replace(urlRegex, (url) => {
    return `<a href="${url}" rel="noreferrer noopener" target="_blank">${t(['urlChecklist'])}</a>`
  })
}

class Confirmation extends Step {
  sendData = false

  constructor(props) {
    super(props)
    this.cancelReasons = createOptions(['options', 'cancelReasons'], ['no_need', 'illness', 'personal', 'other'])
  }

  initialModal(): Map<string, mixed> {
    return Map({ cancel: false })
  }

  validations(_trans: PrefixType) {
    return {
      cancellationReason: nonEmptyString(),
      cancellationComment: nonEmptyString()
    }
  }

  updateHandler = (event) => {
    event.preventDefault()
    const { changeStep } = this.props
    changeStep('update')
  }

  toggleCancelHandler = (event) => {
    event.preventDefault()
    this.mergeModal(
      [],
      Map({
        cancellationReason: null,
        cancellationComment: null
      })
    )
    this.setModal(['cancel'], !this.getModal(['cancel']))
  }

  cancelEventHandler = (event) => {
    event.preventDefault()
    cancelLead(this.getHandler()).then((newData) => {
      const { data, changeStep } = this.props
      data.replace(this.getHandler()).then(() => {
        data
          .update(() => newData)
          .then(() => {
            return changeStep('cancelled').then((newStep) => [newStep])
          })
      })
    })
  }

  render() {
    const data = this.getHandler()
    const appointment = data.get('appointment')
    if (!appointment) return 'Loading'

    const inputSetProps = this.inputSetProps()
    const advisor = appointment.get('user')
    const advisorGender = advisor.get('gender')
    const branch = appointment.get('branch')
    const topics = appointment.get('topics')
    const medium = appointment.get('medium')

    const checklistItems = topics
      .reduce((list, topic) => list.concat(topic.get('checklistItems')), List())
      .map((entry) => {
        return replaceLinks(entry.get(i18n.locale))
      })
      .toSet()
      .toList()

    return (
      <Application title={t(['title'])} name={i18n.trans(['tve', 'title'])}>
        <div className="atp-summary">
          <h3>{t(['when'])}</h3>
          <p>
            {t(['whenTime'], {
              time: dayWithDateTime(parseISO(appointment.get('meetingStartTime'))),
              until: formatTime(parseISO(appointment.get('meetingEndTime')))
            })}
          </p>

          <h3>{t(['where'])}</h3>
          {medium == 'bank' && branch.get('name')}
          {medium == 'phone' && t(['phone'])}
          {medium == 'personal' && t(['personal'])}
          <br />
          <p>
            {(medium == 'bank' || medium == 'personal') && (
              <Fragment>
                {appointment.get('street')}, {appointment.get('zip')} {appointment.get('city')}
              </Fragment>
            )}

            {medium == 'bank' && (
              <Fragment>
                <br />
                <a
                  href={`https://www.google.ch/maps?q=${branchAddress(branch)}`}
                  target="_blank"
                  rel="noopener noreferrer">
                  {t('showOnMap')}
                </a>
              </Fragment>
            )}
            {medium == 'personal' && (
              <Fragment>
                <br />
                <a
                  href={`https://www.google.ch/maps?q=${branchAddress(appointment)}`}
                  target="_blank"
                  rel="noopener noreferrer">
                  {t('showOnMap')}
                </a>
              </Fragment>
            )}
          </p>
          {!!advisorGender && <h3>{t(['advisorTitle', advisorGender])}</h3>}
          <div className="atp-summary__advisor">
            {advisor.get('pictureUrl') && <img src={advisor.get('pictureUrl')} />}
            <div>
              <h3>
                {advisor.get('firstName')} {advisor.get('lastName')}
              </h3>
              <span>{advisor.get('title')}</span>
            </div>
          </div>

          <h3 className="atp-margin-bottom-xs">{t(['topics'])}</h3>
          {topics.map((topic) => {
            const description = topic.get('description')
            return (
              <Fragment key={topic.get('id')}>
                <h4>{topic.get('name')}</h4>
                {description && <p className="atp-small">{description}</p>}
              </Fragment>
            )
          })}
          {checklistItems.size > 0 && (
            <Fragment>
              <br />
              <h3 className="atp-margin-bottom-xs">{t('checklist')}</h3>
              <p>{t(['checklistInfo'])}</p>
              <ul>
                {checklistItems.map((entry, index) => {
                  return <li key={index} dangerouslySetInnerHTML={{ __html: entry }} />
                })}
              </ul>
            </Fragment>
          )}
          <p>{t(['uploadDocuments'])}</p>
          {topics.find((topic) => topic.get('code') == 'finance') && (
            <div className="atp-notification atp-margin-bottom-xs">
              <span>{t(['financeUploadInfo'])}</span>
            </div>
          )}
          <DocumentList data={data} />

          <div className="atp-margin-bottom-m">
            <h3 className="atp-margin-top-m">{t(['changeReverse'])}</h3>
            {this.getModal(['cancel']) ? (
              <form onSubmit={this.cancelEventHandler}>
                <h3 className="atp-margin-top-xs">Stornieren</h3>
                <Textarea {...inputSetProps(['cancellationComment'])} />
                <button
                  className="atp-btn atp-btn--right-icon atp-btn--icon-add atp-margin-top-xs atp-margin-right-xxs"
                  onClick={this.toggleCancelHandler}>
                  {i18n.trans(['buttons', 'cancel'])}
                </button>
                <button
                  className="atp-btn atp-btn--right-icon atp-btn--icon-add atp-margin-top-xs"
                  onClick={this.cancelEventHandler}>
                  {i18n.trans(['buttons', 'reverse'])}
                </button>
              </form>
            ) : (
              <Fragment>
                <button
                  className="atp-btn atp-btn--right-icon atp-btn--icon-add atp-margin-top-xs atp-margin-right-xxs"
                  onClick={this.updateHandler}>
                  {i18n.trans(['buttons', 'move'])}
                </button>
                <button
                  className="atp-btn atp-btn--right-icon atp-btn--icon-add atp-margin-top-xs"
                  onClick={this.toggleCancelHandler}>
                  {i18n.trans(['buttons', 'reverse'])}
                </button>
              </Fragment>
            )}
          </div>
        </div>
      </Application>
    )
  }
}

export default Confirmation
